import { useMemo } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import {
  parseIncompletePhoneNumber,
  parsePhoneNumber as parseCompletePhoneNumber,
} from 'libphonenumber-js';
import * as yup from 'yup';

import { US_COUNTRY_CODE, US_PHONE_NUMBER_MAX_LENGTH } from './constants';

// ────────────────────────────────────────────────────────────────────────────────
export const useFormValidation = (hasZipCode: boolean) => {
  const i18n = useYupValidationMessages();
  const validations = getValidations(i18n, hasZipCode);

  return validations;
};

const useYupValidationMessages = () => {
  const { formatMessage } = useIntl();
  const i18n = useMemo(() => {
    return {
      firstNameRequired: formatMessage(messages.formFirstNameRequired),
      firstNameValidChars: formatMessage(messages.formInvalidCharacters, {
        field_name: 'First',
      }),
      firstNameMaxLength: formatMessage(messages.formMaxNameLength, {
        field_name: 'First',
      }),
      lastNameRequired: formatMessage(messages.formLastNameRequired),
      lastNameValidChars: formatMessage(messages.formInvalidCharacters, {
        field_name: 'Last',
      }),
      lastNameMaxLength: formatMessage(messages.formMaxNameLength, {
        field_name: 'Last',
      }),
      phoneNumberRequired: formatMessage(messages.formPhoneNumberRequired),
      phoneNumberValid: formatMessage(messages.formValidationPhoneNumber),
      zipCodeRequired: formatMessage(messages.formZipCodeRequired),
      zipCodeValid: formatMessage(messages.formValidationZipCode),
      passwordMinLength: formatMessage(messages.formMinPasswordLength),
      passwordsMatch: formatMessage(messages.formConfirmPasswordMatch),
    };
  }, [formatMessage]);

  return i18n;
};

type I18nMessages = ReturnType<typeof useYupValidationMessages>;
const getValidations = (i18n: I18nMessages, hasZipCode: boolean) =>
  yup.object({
    firstName: yup
      .string()
      .required(i18n.firstNameRequired)
      .matches(/^[\sA-Za-z]+$/, i18n.firstNameValidChars)
      .max(50, i18n.firstNameMaxLength),
    lastName: yup
      .string()
      .required(i18n.lastNameRequired)
      .matches(/^[\sA-Za-z]+$/, i18n.lastNameValidChars)
      .max(50, i18n.lastNameMaxLength),
    phoneNumber: yup
      .string()
      .required(i18n.phoneNumberRequired)
      .test('test-valid-phone-number', function (phoneNumber) {
        const { path, createError } = this;
        const parsedPhoneNumber = parseIncompletePhoneNumber(phoneNumber ?? '');
        const isValidNumber =
          parsedPhoneNumber.length === US_PHONE_NUMBER_MAX_LENGTH
            ? parseCompletePhoneNumber(parsedPhoneNumber, US_COUNTRY_CODE).isValid()
            : true;

        return isValidNumber || createError({ path, message: i18n.phoneNumberValid });
      })
      .matches(/^\d+$/, i18n.phoneNumberValid)
      .min(10, i18n.phoneNumberValid)
      .max(10, i18n.phoneNumberValid),
    zipCode: hasZipCode
      ? yup
          .string()
          .required(i18n.zipCodeRequired)
          .min(3, i18n.zipCodeValid)
          .max(8, i18n.zipCodeValid)
          .matches(/^[\dA-Z]*$/, i18n.zipCodeValid)
      : yup.string(),
    password: yup.lazy((val: string) => {
      if (val.length > 0) {
        return yup.string().min(8, i18n.passwordMinLength);
      }

      return yup.string();
    }),
    confirmPassword: yup.string().when('password', {
      is: (val: string) => val !== '',
      /* eslint-disable unicorn/no-thenable */
      then: (schema) => schema.oneOf([yup.ref('password')], i18n.passwordsMatch),
    }),
  });

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly formFirstNameRequired: NoMessageValues;
  readonly formInvalidCharacters: { readonly field_name: MessageValue };
  readonly formMaxNameLength: { readonly field_name: MessageValue };
  readonly formLastNameRequired: NoMessageValues;
  readonly formPhoneNumberRequired: NoMessageValues;
  readonly formValidationPhoneNumber: NoMessageValues;
  readonly formZipCodeRequired: NoMessageValues;
  readonly formValidationZipCode: NoMessageValues;
  readonly formMinPasswordLength: NoMessageValues;
  readonly formConfirmPasswordMatch: NoMessageValues;
}>({
  formFirstNameRequired: {
    defaultMessage: 'First name is required',
    description: 'Form | First Name Required',
  },
  formInvalidCharacters: {
    defaultMessage: '{field_name} name has invalid characters',
    description: 'Form | Invalid Characters',
  },
  formMaxNameLength: {
    defaultMessage: '{field_name} name should be no more than 50 characters',
    description: 'Form | Max Name Length',
  },
  formLastNameRequired: {
    defaultMessage: 'Last name is required',
    description: 'Form | Last Name Required',
  },
  formPhoneNumberRequired: {
    defaultMessage: 'Phone number is required',
    description: 'Form | Phone Number Required',
  },
  formValidationPhoneNumber: {
    defaultMessage: 'Please enter a valid phone number (10 digits)',
    description: 'Form | Validation Phone Number',
  },
  formZipCodeRequired: {
    defaultMessage: 'Zip code is required',
    description: 'Form | Zip Code Required',
  },
  formValidationZipCode: {
    defaultMessage: 'Zip code is invalid',
    description: 'Form | Validation Zip Code',
  },
  formMinPasswordLength: {
    defaultMessage: 'Password should be at least 8 characters',
    description: 'Form | Min Password Length',
  },
  formConfirmPasswordMatch: {
    defaultMessage: 'Passwords must match',
    description: 'Form | Confirm Password Match',
  },
});
